import * as React from 'react';

export interface CheckboxOption {
  value: number;
  label: string;
}

export interface CheckboxGroupProps {
  options: CheckboxOption[];
  selectedValues: number[];
  onChange: (ids: number[]) => void;
  color?: 'purple' | 'blue' | 'green';
  columns?: 1 | 2 | 3;
}

const colorClasses = {
  purple: {
    selected: 'bg-purple-50 border-purple-300',
    checkbox: 'bg-purple-600 border-purple-600 text-white',
  },
  blue: {
    selected: 'bg-blue-50 border-blue-300',
    checkbox: 'bg-blue-600 border-blue-600 text-white',
  },
  green: {
    selected: 'bg-green-50 border-green-300',
    checkbox: 'bg-green-600 border-green-600 text-white',
  },
};

export const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
  options,
  selectedValues,
  onChange,
  color = 'purple',
  columns = 2,
}) => {
  const colors = colorClasses[color];
  const gridClass =
    columns === 1
      ? 'grid-cols-1'
      : columns === 2
        ? 'grid-cols-2'
        : 'grid-cols-3';

  const handleChange = (value: number) => {
    const newValues = selectedValues.includes(value)
      ? selectedValues.filter(v => v !== value)
      : [...selectedValues, value];
    onChange(newValues);
  };

  return (
    <div className={`grid ${gridClass} gap-2`}>
      {options.map(option => {
        const isSelected = selectedValues.includes(option.value);
        return (
          <label
            key={option.value}
            className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
              isSelected
                ? colors.selected
                : 'bg-white border-gray-200 hover:bg-gray-50'
            }`}
          >
            <input
              type='checkbox'
              checked={isSelected}
              onChange={() => handleChange(option.value)}
              className='sr-only'
            />
            <span
              className={`w-4 h-4 mr-2 rounded border flex items-center justify-center ${
                isSelected ? colors.checkbox : 'border-gray-300'
              }`}
            >
              {isSelected && (
                <svg
                  className='w-3 h-3'
                  fill='currentColor'
                  viewBox='0 0 20 20'
                >
                  <path
                    fillRule='evenodd'
                    d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                    clipRule='evenodd'
                  />
                </svg>
              )}
            </span>
            <span className='text-sm text-gray-700'>{option.label}</span>
          </label>
        );
      })}
    </div>
  );
};
